import type { SupabaseClient } from '@supabase/supabase-js'
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

export type SlashCommandTier = 'A' | 'B'

export type SlashArgSchema = {
  name: string
  type: 'string' | 'number' | 'campaign' | 'page' | 'period' | 'amount'
  required: boolean
  description?: string
}

export type SlashCommandUiAction =
  | { type: 'new-thread' }
  | { type: 'stop-streaming' }
  | { type: 'open-help'; commandName?: string }
  | { type: 'open-checkout'; url: string }

export type SlashCommandResult =
  | { kind: 'success'; message: string; data?: unknown }
  | { kind: 'error'; message: string }
  | { kind: 'navigate'; path: string }
  | { kind: 'llm-routed'; prompt: string; displayMessage?: string }
  | { kind: 'ui-action'; action: SlashCommandUiAction; message?: string }

export type SlashCommandUiHooks = {
  /** Abort the current AI stream (no-op if nothing streaming). */
  stopStreaming?: () => void
  /** Start a fresh chat thread. */
  startNewThread?: () => void
  /** Submit a side question without polluting main thread context. */
  submitSideQuestion?: (prompt: string) => void
}

export type SlashCommandContext = {
  workspaceId: string
  userId: string
  router: AppRouterInstance
  supabase: SupabaseClient
  /** Optional UI hooks injected by ChatInterface for client-side actions. */
  ui?: SlashCommandUiHooks
  /** Whether the current user is an Atrium admin (gates `adminOnly` UI). */
  isAdmin?: boolean
}

export type SlashCommand = {
  name: string
  aliases?: string[]
  description: string
  tier: SlashCommandTier
  args: SlashArgSchema[]
  custom?: boolean
  /**
   * Required for Tier A commands. Optional for Tier B (which always route
   * through `promptTemplate`); if provided on a Tier B command it is ignored.
   */
  handler?: (args: Record<string, unknown>, ctx: SlashCommandContext) => Promise<SlashCommandResult>
  adminOnly?: boolean
  /** For Tier B commands: build the natural-language prompt sent to the LLM. */
  promptTemplate?: (args: Record<string, unknown>) => string
  /**
   * For commands that resolve a resource: return the user-visible message text
   * with the resolved resource replaced by a mention token. When set, the chat
   * pipeline uses this output as the thread message instead of the raw input.
   * Return null to fall back to the raw input.
   */
  displayMessageBuilder?: (args: Record<string, unknown>, ctx: SlashCommandContext) => Promise<string | null>
}

function normalizeCommandName(name: string): string {
  return name.trim().replace(/^\//, '').toLowerCase()
}

function scoreCommand(cmd: SlashCommand, query: string): number {
  const q = query.trim().replace(/^\//, '').toLowerCase()
  if (!q) return 1

  const candidates = [
    cmd.name,
    ...(cmd.aliases ?? []),
    cmd.description,
    ...cmd.args.map(arg => arg.name),
  ].map(value => value.toLowerCase())

  let best = 0
  for (const candidate of candidates) {
    if (candidate === q) best = Math.max(best, 100)
    else if (candidate.startsWith(q)) best = Math.max(best, 80 - (candidate.length - q.length))
    else if (candidate.includes(q)) best = Math.max(best, 55 - candidate.indexOf(q))
    else {
      let qi = 0
      for (const ch of candidate) {
        if (ch === q[qi]) qi += 1
        if (qi === q.length) break
      }
      if (qi === q.length) best = Math.max(best, 30 - (candidate.length - q.length))
    }
  }

  return best
}

export class SlashCommandRegistry {
  private commands = new Map<string, SlashCommand>()
  private aliasMap = new Map<string, string>()

  register(cmd: SlashCommand): void {
    const name = normalizeCommandName(cmd.name)
    const existing = this.commands.get(name)
    for (const alias of existing?.aliases ?? []) {
      this.aliasMap.delete(alias)
    }

    const normalized: SlashCommand = {
      ...cmd,
      name,
      aliases: cmd.aliases?.map(normalizeCommandName),
    }

    this.commands.set(name, normalized)
    for (const alias of normalized.aliases ?? []) {
      this.aliasMap.set(alias, name)
    }
  }

  get(name: string): SlashCommand | undefined {
    const normalized = normalizeCommandName(name)
    return this.commands.get(this.aliasMap.get(normalized) ?? normalized)
  }

  unregister(name: string): void {
    const normalized = normalizeCommandName(name)
    const existing = this.commands.get(normalized)
    if (!existing) return
    for (const alias of existing.aliases ?? []) {
      this.aliasMap.delete(alias)
    }
    this.commands.delete(normalized)
  }

  list(opts?: { includeAdmin?: boolean }): SlashCommand[] {
    return Array.from(this.commands.values())
      .filter(cmd => opts?.includeAdmin || !cmd.adminOnly)
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  search(query: string, opts?: { includeAdmin?: boolean }): SlashCommand[] {
    return this.list(opts)
      .map(cmd => ({ cmd, score: scoreCommand(cmd, query) }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score || a.cmd.name.localeCompare(b.cmd.name))
      .map(item => item.cmd)
  }
}

export const slashCommandRegistry = new SlashCommandRegistry()
